/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Component, ChangeDetectorRef, Inject, inject, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {APP_CONFIG} from '../../app.config';
import {Site} from "../../model/Site";
import {SitesService} from "../../services/sites.service";
import {EndorsementService} from "../../services/endorsement.service";
import {Endorsement} from "../../model/Endorsement";
import {DatePipe} from "@angular/common";
import {DrivingLevel} from "../../model/DrivingLevel";
import {DrivingLevelsService} from "../../services/driving-levels.service";
import {TrainingCourse} from "../../model/TrainingCourse";
import {TrainingService} from "../../services/training.service";
import {GovermentEndorsementsService} from "../../services/goverment-endorsements.service";
import {GovernmentEndorsements} from "../../model/GovernmentEndorsements";
import {RegionService} from "../../services/region.service";
import {Region} from "../../model/Region";
import {NgxSpinnerService} from 'ngx-spinner';

@Component({
    selector: 'app-view-report',
    templateUrl: './view-report.component.html',
    styleUrls: ['./view-report.component.less'],
    standalone: false
})
export class ViewReportComponent implements OnInit {

  reportForm: UntypedFormGroup;

  public showSites = false;
  public showStartDate = false;
  public showEndDate = false;
  public showEndDateFull = false;
  public showSubmit = false;
  public showEndorsement = false;
  public showGovEndorsements = false;
  public showCourses = false;
  public showDrivingLevels = false;
  public showRegion = false;
  public showSelectAllDates = false;
  public hideDateFields = false;

  public sites: Site[];
  public endorsements: Endorsement[];
  public driverLevels: DrivingLevel[];
  public courses: TrainingCourse[];
  public regions: Region[];
  public govEndorsements: GovernmentEndorsements[];
  public powerBiUrl: string;
  private reportUrls: {};

  public dropdownDemo = {
    selection: 'Choose an Option',
    options: [
      {name: 'Please Select', value: ''},
      {name: 'Accidents By Site Date', value: 'AccidentsBySiteDate'},
      {name: 'Accidents Passengers and Pedestrians', value: 'AccidentPassengers'},
      {name: 'Accidents Per Million Miles', value: 'AccidentsPerMillionMiles'},
      {name: 'Driver Level By Site', value: 'DriverLvlBySiteDate'},
      {name: 'Drivers Inactivated', value: 'DriversInactivated'},
      {name: 'Drivers Suspended', value: 'DriversSuspended'},
      {name: 'Endorsement By Site and Date', value: 'EndorsementBySiteDate'},
      {name: 'Expired Driver Levels', value: 'ExpiredDriveLevelsBySite'},
      {name: 'Expired Endorsements By Site', value: 'ExpiredEndorsementBySite'},
      {name: 'Expired Gov. Endorsements By Region', value: 'ExpiredGovEndorsementsByRegion'},
      {name: 'Expired Training By Site', value: 'ExpiredTrainingBySite'},
      {name: 'Gov. Endorsements By Site', value: 'GovEndorsementBySiteDate'},
      {name: 'Training By Site', value: 'TrainingBySiteDate'},
      {name: 'Violations By Site', value: 'ViolationsBySiteandDate'}
    ]
  };



  constructor(private fb: UntypedFormBuilder,
              private siteService: SitesService,
              private endorsementService: EndorsementService,
              private driverLevelService: DrivingLevelsService,
              private trainingService: TrainingService,
              private regionService: RegionService,
              private govEndorsementService: GovermentEndorsementsService,
              private spinnerService: NgxSpinnerService,
              private cdr: ChangeDetectorRef,
              @Inject(APP_CONFIG) appConfig: any) {
    this.powerBiUrl = appConfig.powerBiUrl;
    this.reportUrls = appConfig.reportUrls;

    this.createForm();
  }

  ngOnInit() {
  }

  createForm() {
    this.reportForm = this.fb.group({
      reportName: null,
      sitesCtrl: null,
      endorsements: null,
      regionsCtrl: null,
      govEndorsementCtrl: null,
      courses: null,
      drivingLevels: null,
      startDate: null,
      endDateFull: null,
      endDateMonth: null,
      endDateYear: null,
      selectAllDates: false
    });
  }


  private unRequiredFormCtrl() {

    this.reportForm.controls["sitesCtrl"].clearValidators();
    this.reportForm.controls["endorsements"].clearValidators();
    this.reportForm.controls["regionsCtrl"].clearValidators();
    this.reportForm.controls["govEndorsementCtrl"].clearValidators();
    this.reportForm.controls["courses"].clearValidators();
    this.reportForm.controls["drivingLevels"].clearValidators();
    this.reportForm.controls["startDate"].clearValidators();
    this.reportForm.controls["endDateFull"].clearValidators();
    this.reportForm.controls["endDateMonth"].clearValidators();
    this.reportForm.controls["endDateYear"].clearValidators();
  }

  updateForm(report) {
    this.spinnerService.show();
    this.disableFormItems();
    this.unRequiredFormCtrl();
    var reportName = report.target.value;
    this.showSelectAllDates = false;

    switch(reportName) {
      case "AccidentsPerMillionMiles":
        this.doLookupFields('sites');
        this.showSites = true;
        this.showEndDateFull = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['endDateFull'].setValidators(Validators.required);
        break;
      case "DriverLvlBySiteDate":
        this.doLookupFields('sites');
        this.doLookupFields('driverLevel');
        this.showSites = true;
        this.showStartDate = true;
        this.showDrivingLevels = true;
        this.showEndDateFull = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['startDate'].setValidators(Validators.required);
        this.reportForm.controls['drivingLevels'].setValidators(Validators.required);
        this.reportForm.controls['endDateFull'].setValidators(Validators.required);
        break;
      case "DriversInactivated":
        this.showSubmit = true;
        break;
      case "DriversSuspended":
        this.showSubmit = true;
        break;
      case "EndorsementBySiteDate":
        this.doLookupFields('sites');
        this.doLookupFields('endorsement');
        this.showStartDate = true;
        this.showEndDateFull = true;
        this.showEndorsement = true;
        this.showSites = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['startDate'].setValidators(Validators.required);
        this.reportForm.controls['endorsements'].setValidators(Validators.required);
        this.reportForm.controls['endDateFull'].setValidators(Validators.required);
        break;
      case "ExpiredDriveLevelsBySite":
        this.doLookupFields('sites');
        this.showSites = true;
        this.showEndDate = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['endDateMonth'].setValidators(Validators.required);
        this.reportForm.controls['endDateYear'].setValidators(Validators.required);
        break;
      case "ExpiredGovEndorsementsByRegion":
        this.doLookupFields('regions');
        this.showRegion = true;
        this.showEndDate = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['regionsCtrl'].setValidators(Validators.required);
        this.reportForm.controls['endDateMonth'].setValidators(Validators.required);
        this.reportForm.controls['endDateYear'].setValidators(Validators.required);
        break;
      case "ExpiredEndorsementBySite":
        this.doLookupFields('sites');
        this.showSites = true;
        this.showEndDate = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['endDateMonth'].setValidators(Validators.required);
        this.reportForm.controls['endDateYear'].setValidators(Validators.required);
        break;
      case "ExpiredTrainingBySite":
        this.doLookupFields('sites');
        this.showSites = true;
        this.showEndDate = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['endDateMonth'].setValidators(Validators.required);
        this.reportForm.controls['endDateYear'].setValidators(Validators.required);
        break;
      case "GovEndorsementBySiteDate":
        this.doLookupFields('govEndorsements');
        this.doLookupFields('regions');
        this.showGovEndorsements = true;
        this.showRegion = true;
        this.showStartDate = true;
        this.showEndDateFull = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['govEndorsementCtrl'].setValidators(Validators.required);
        this.reportForm.controls['regionsCtrl'].setValidators(Validators.required);
        this.reportForm.controls['startDate'].setValidators(Validators.required);
        this.reportForm.controls['endDateFull'].setValidators(Validators.required);
        break;
      case "TrainingBySiteDate":
        this.doLookupFields('sites');
        this.doLookupFields('course');
        this.showSites = true;
        this.showCourses = true;
        this.showStartDate = true;
        this.showEndDateFull = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['courses'].setValidators(Validators.required);
        this.reportForm.controls['startDate'].setValidators(Validators.required);
        this.reportForm.controls['endDateFull'].setValidators(Validators.required);
        break;
      case "AccidentPassengers":
      case "AccidentsBySiteDate":
      case "ViolationsBySiteandDate":
        this.doLookupFields('sites');
        this.showSites = true;
        this.showStartDate = true;
        this.showEndDateFull = true;
        this.showSubmit = true;
        this.showSelectAllDates = true;
        this.reportForm.controls['sitesCtrl'].setValidators(Validators.required);
        this.reportForm.controls['startDate'].setValidators(Validators.required);
        this.reportForm.controls['endDateFull'].setValidators(Validators.required);
        break;
      default:
        this.disableFormItems();
    }
    Object.keys(this.reportForm.controls).forEach(key => {
      this.reportForm.controls[key].updateValueAndValidity();
    });
    this.cdr.detectChanges();
    this.spinnerService.hide();
  }

  showReport() {
    if (this.reportForm.invalid) {
      const invalidFields = [];
      const controls = this.reportForm.controls;
      const fieldNames = {
        sitesCtrl: 'Site',
        endorsements: 'Endorsements',
        regionsCtrl: 'Regions',
        govEndorsementCtrl: 'Gov. Endorsements and Region',
        courses: 'Training Courses',
        drivingLevels: 'Driving Levels',
        startDate: 'Start Date',
        endDateFull: 'End Date',
        endDateMonth: 'Month',
        endDateYear: 'Year'
      };
      for (const name in controls) {
        if (controls[name].invalid) {
          invalidFields.push(fieldNames[name] || name);
        }
      }
      alert('Please fill in all required fields: ' + invalidFields.join(', '));
      return;
    }

    this.spinnerService.show();
    const frmReport = this.reportForm.value;
    const reportName = (<HTMLSelectElement>document.getElementById("reportName")).value;
    let url = "";
    let reportUrl = "";

    url = this.powerBiUrl;

    switch (reportName) {
      case "AccidentsPerMillionMiles":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy');
        break;
      case "DriverLvlBySiteDate":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:StartDate=" + new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy')
          + "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy') + "&rp:DrvLvlID=" + frmReport.drivingLevels.toString();
        break;
      case "DriversInactivated":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl;
        break;
      case "DriversSuspended":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl;
        break;
      case "EndorsementBySiteDate":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:EndDate=" +
          new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy') + "&rp:StartDate=" +
          new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy') +
          "&rp:EndorseID=" + frmReport.endorsements.toString();
        break;
      case "ExpiredDriveLevelsBySite":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:Yr=" + frmReport.endDateYear +
          "&rp:mth=" + frmReport.endDateMonth;
        break;
      case 'ExpiredGovEndorsementsByRegion':
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Regions_str=" + frmReport.regionsCtrl.toString() + "&rp:Yr=" +
          frmReport.endDateYear + "&rp:mth=" + frmReport.endDateMonth;
        break;
      case "ExpiredEndorsementBySite":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:Yr=" +
          frmReport.endDateYear + "&rp:mth=" + frmReport.endDateMonth;
        break;
      case "ExpiredTrainingBySite":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:Yr=" + frmReport.endDateYear +
          "&rp:mth=" + frmReport.endDateMonth;
        break;
      case "GovEndorsementBySiteDate":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Regions_str=" + frmReport.regionsCtrl.toString() + "&rp:StartDate=" +
          new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy') +
          "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy') +
          "&rp:GovEndorseID=" + frmReport.govEndorsementCtrl.toString();
        break;
      case "TrainingBySiteDate":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:StartDate=" + new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy') +
          "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy') +
          "&rp:TrainID=" + frmReport.courses.toString();
        break;
      case "AccidentPassengers":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:StartDate=" + new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy') + "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy');
        break;
      case "AccidentsBySiteDate":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:StartDate=" + new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy') + "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy');
        break;
      case "ViolationsBySiteandDate":
        reportUrl = this.reportUrls[reportName]
        url = url + reportUrl + "rp:Sites_str=" + frmReport.sitesCtrl.toString() + "&rp:StartDate=" + new DatePipe('en-US').transform(frmReport.startDate, 'M/d/yyyy') +
          "&rp:EndDate=" + new DatePipe('en-US').transform(frmReport.endDateFull, 'M/d/yyyy');
        break;
      default:
        this.disableFormItems();
        this.spinnerService.hide();
        return;
    }
    window.open(url, '_blank');
    this.spinnerService.hide();
  }

  private doLookupFields(fieldName) {
    this.spinnerService.show();
    switch (fieldName){
      case 'sites':
        this.sites = [];
        this.siteService.getAllSites().subscribe(value => {
            this.sites = value;
            this.cdr.detectChanges();
          },
          err => {
            console.log(err);
            alert(err);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
        break;
      case 'course':
        this.trainingService.getAllTraining().subscribe(value => {
            this.courses = value;
            this.cdr.detectChanges();
          },
          err => {
            console.log(err);
            alert(err);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
        break;
      case 'endorsement':
        this.endorsementService.getAllEndorsements().subscribe(value => {
            this.endorsements = value;
            this.cdr.detectChanges();
          },
          err => {
            console.log(err);
            alert(err);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
        break;
      case 'driverLevel':
        this.driverLevelService.getAllDrivingLevels().subscribe(value => {
            this.driverLevels = value;
            this.cdr.detectChanges();
          },
          err => {
            console.log(err);
            alert(err);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
        break;
      case 'regions':
        this.regionService.getAll().subscribe(value => {
            this.regions = value;
            this.cdr.detectChanges();
          },
          err => {
            console.log(err);
            alert(err);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
        break;
      case 'govEndorsements':
        this.govEndorsementService.getAll().subscribe(value => {
            this.govEndorsements = value;
            this.cdr.detectChanges();
          },
          err => {
            console.log(err);
            alert(err);
            this.spinnerService.hide();
          },
          () => {
            this.spinnerService.hide();
          }
        );
        break;
      default:
        break;
    }
  }

  private disableFormItems() {
    this.showSites = false;
    this.showDrivingLevels = false;
    this.showEndDate = false;
    this.showEndDateFull = false;
    this.showStartDate = false;
    this.showEndorsement = false;
    this.showCourses = false;
    this.showRegion = false;
    this.showGovEndorsements = false;
  }

  onSelectAllDatesChange(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const datePipe = new DatePipe('en-US');
    this.hideDateFields = isChecked;
    if (isChecked) {
      const patchValues: any = {};
      if (this.reportForm.controls['endDateFull']) {
        patchValues.endDateFull = datePipe.transform(new Date(2100, 11, 31), 'yyyy-MM-dd')
      }
      if (this.reportForm.controls['startDate']) {
        patchValues.startDate = datePipe.transform(new Date(1900, 0, 1), 'yyyy-MM-dd');
      }
      if (this.reportForm.controls['endDateMonth']) {
        patchValues.endDateMonth = 12;
      }
      if (this.reportForm.controls['endDateYear']) {
        patchValues.endDateYear = 2100;
      }
      this.reportForm.patchValue(patchValues);
    } else {
      const patchValues: any = {};
      if (this.reportForm.controls['endDateFull']) {
        patchValues.endDateFull = null;
      }
      if (this.reportForm.controls['startDate']) {
        patchValues.startDate = null;
      }
      if (this.reportForm.controls['endDateMonth']) {
        patchValues.endDateMonth = null;
      }
      if (this.reportForm.controls['endDateYear']) {
        patchValues.endDateYear = null;
      }
      this.reportForm.patchValue(patchValues);
    }
  }

}
